"use client";

import { ArrowRight, Phone, Minus, Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Office() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const serviceAreas = [{
    name: "COLLEGEVILLE",
    number: "01"
  }, {
    name: "PHOENIXVILLE",
    number: "02"
  }, {
    name: "KING OF PRUSSIA",
    number: "03"
  }, {
    name: "NORRISTOWN",
    number: "04"
  }, {
    name: "LIMERICK",
    number: "05"
  }, {
    name: "POTTSTOWN",
    number: "06"
  }, {
    name: "BLUE BELL",
    number: "07"
  }];
  const mapPins = [{
    name: "COLLEGEVILLE",
    coords: "40.1915°N / 75.4509°W",
    top: "62%",
    left: "70%"
  }, {
    name: "PHOENIXVILLE",
    coords: "40.1391°N / 75.5119°W",
    top: "30%",
    left: "58%"
  }, {
    name: "KING OF PRUSSIA",
    coords: "40.0825°N / 75.3917°W",
    top: "44%",
    left: "44%"
  }, {
    name: "NORRISTOWN",
    coords: "40.1208°N / 75.3498°W",
    top: "74%",
    left: "56%"
  }, {
    name: "LIMERICK",
    coords: "40.2244°N / 75.5278°W",
    top: "32%",
    left: "30%"
  }, {
    name: "POTTSTOWN",
    coords: "40.2456°N / 75.6464°W",
    top: "36%",
    left: "82%"
  }];
  return <section data-section-id="1276" id="visit-our-collegeville-office" className="relative bg-obsidian-void text-white py-24 md:py-32 overflow-hidden dark">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-700 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="flex items-center gap-3 mb-8">
              <div className="h-px w-6 bg-blueprint-amber" />
              <span className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-steel-edge">
                LOCATION 01 
              </span>
            </div>

            <h2 className="font-default uppercase font-normal leading-[1.0] tracking-tighter text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] mb-10">
              OUR{" "}
              <em className="not-italic">
                <span className="italic text-blueprint-amber">HQ</span>
              </em>
              .
            </h2>

            <div className="mb-6">
              <h4 className="font-default uppercase font-medium text-2xl md:text-3xl tracking-normal leading-tight">
                24 DONNY BROOK WAY
                <br />
                COLLEGEVILLE, PA 19426
              </h4>
            </div>

            <p className="font-mono text-xs font-semibold uppercase tracking-[0.15em] text-steel-edge mb-10">
              40.1915°N / 75.4509°W
            </p>

            <div className="border-t border-steel-edge pt-8 mb-10">
              <p className="font-mono text-xs font-semibold uppercase tracking-[0.18em] text-steel-edge mb-6">
                SERVICE REGIONS
              </p>
              <ul className="space-y-3">
                {serviceAreas.map((area, i) => <li data-index={i} key={area.name} className="flex items-baseline gap-4 group">
                    <span className="font-mono text-xs font-semibold text-blueprint-amber tracking-[0.15em] w-6">
                      {area.number}
                    </span>
                    <span className="font-default uppercase text-lg md:text-xl tracking-normal group-hover:text-blueprint-amber transition-colors duration-200">
                      {area.name}
                    </span>
                  </li>)}
              </ul>
            </div>

            <div className="flex flex-col gap-4">
              <Link to="/ConnectWithUsPage" className="inline-flex items-center gap-2 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-blueprint-amber hover:text-white transition-colors duration-200 underline underline-offset-[6px] decoration-1">
                GET DIRECTIONS <ArrowRight className="size-3.5" />
              </Link>
              <Link to="tel:610-342-5237" className="inline-flex items-center gap-2 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-blueprint-amber hover:text-white transition-colors duration-200 underline underline-offset-[6px] decoration-1">
                <Phone className="size-3.5" /> 610-342-5237
              </Link>
            </div>
          </div>

          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-700 ease-out delay-150 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="relative border border-steel-edge aspect-[16/10] lg:aspect-auto lg:h-full lg:min-h-[560px] bg-structural-slate overflow-hidden">
              <div className="absolute top-4 left-4 z-20 font-mono text-[0.6875rem] font-semibold uppercase tracking-[0.15em] text-steel-edge">
                SITE MAP 
              </div>
              <div className="absolute top-4 right-4 z-20 flex gap-2">
                <Button variant="ghost" size="icon" className="size-8 bg-obsidian-void/80 border border-steel-edge text-white hover:bg-blueprint-amber hover:text-black hover:border-blueprint-amber rounded-lg" aria-label="Zoom in">
                  <Plus className="size-4" />
                </Button>
                <Button variant="ghost" size="icon" className="size-8 bg-obsidian-void/80 border border-steel-edge text-white hover:bg-blueprint-amber hover:text-black hover:border-blueprint-amber rounded-lg" aria-label="Zoom out">
                  <Minus className="size-4" />
                </Button>
              </div>

              <div className="absolute inset-0 bg-obsidian-void">
                <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none" viewBox="0 0 100 100">
                  {[15, 30, 45, 60, 75, 90].map((y, i) => <line data-index={i} key={`h-${y}`} x1="0" y1={y} x2="100" y2={y} stroke="hsl(215 15% 25%)" strokeWidth="0.15" />)}
                  {[10, 25, 40, 55, 70, 85].map((x, i) => <line data-index={i} key={`v-${x}`} x1={x} y1="0" x2={x} y2="100" stroke="hsl(215 15% 25%)" strokeWidth="0.15" />)}
                  <line x1="0" y1="20" x2="100" y2="32" stroke="hsl(215 15% 35%)" strokeWidth="0.3" />
                  <line x1="0" y1="65" x2="100" y2="78" stroke="hsl(215 15% 35%)" strokeWidth="0.3" />

                  <g stroke="hsl(215 15% 40%)" strokeWidth="0.2" strokeDasharray="0.5 0.5">
                    <line x1="56" y1="50" x2="70" y2="62" />
                    <line x1="56" y1="50" x2="58" y2="30" />
                    <line x1="56" y1="50" x2="44" y2="44" />
                    <line x1="56" y1="50" x2="56" y2="74" />
                    <line x1="56" y1="50" x2="30" y2="32" />
                    <line x1="56" y1="50" x2="82" y2="36" />
                  </g>
                </svg>

                <span className="absolute top-[18%] left-[12%] font-mono text-[0.5rem] uppercase tracking-[0.15em] text-steel-edge/60">
                  PA-422
                </span>
                <span className="absolute top-[68%] left-[78%] font-mono text-[0.5rem] uppercase tracking-[0.15em] text-steel-edge/60">
                  I-76
                </span>
                <span className="absolute top-[8%] left-[42%] font-mono text-[0.5rem] uppercase tracking-[0.15em] text-steel-edge/60">
                  COLLEGEVILLE
                </span>
                <span className="absolute bottom-[6%] left-[44%] font-mono text-[0.5rem] uppercase tracking-[0.15em] text-steel-edge/60">
                  PA, USA
                </span>
              </div>

              <HoverCard openDelay={100} closeDelay={100}>
                <HoverCardTrigger asChild>
                  <div className="absolute z-10 -translate-x-1/2 -translate-y-1/2 cursor-pointer" style={{
                  top: "50%",
                  left: "56%"
                }} role="button" tabIndex={0} aria-label="Moscariello Construction HQ">
                    <span className="relative flex items-center justify-center">
                      <span className="absolute inline-flex h-10 w-10 rounded-lg bg-blueprint-amber/30 animate-ping" />
                      <span className="relative inline-flex size-5 rounded-lg bg-blueprint-amber ring-4 ring-obsidian-void" />
                    </span>
                  </div>
                </HoverCardTrigger>
                <HoverCardContent className="bg-structural-slate border-2 border-blueprint-amber rounded-lg p-4 w-56" side="top">
                  <p className="font-mono text-[0.6875rem] uppercase tracking-[0.18em] text-blueprint-amber mb-2">
                    HQ 
                  </p>
                  <p className="font-default uppercase text-xl tracking-normal mb-2">
                    MOSCARIELLO HQ
                  </p>
                  <p className="font-mono text-[0.6875rem] uppercase tracking-[0.15em] text-steel-edge">
                    40.1915°N / 75.4509°W
                  </p>
                </HoverCardContent>
              </HoverCard>

              {mapPins.map((pin, i) => <div data-index={i} key={pin.name} className="absolute z-10 -translate-x-1/2 -translate-y-1/2" style={{
              top: pin.top,
              left: pin.left
            }}>
                  <HoverCard openDelay={100} closeDelay={100}>
                    <HoverCardTrigger asChild>
                      <div className="block size-2.5 rounded-lg bg-blueprint-amber ring-2 ring-obsidian-void hover:size-3.5 transition-all duration-200 cursor-pointer" role="button" tabIndex={0} aria-label={pin.name} />
                    </HoverCardTrigger>
                    <HoverCardContent className="bg-structural-slate border border-steel-edge rounded-lg p-3 w-48" side="top">
                      <p className="font-default uppercase text-base tracking-normal mb-1">
                        {pin.name}
                      </p>
                      <p className="font-mono text-[0.6875rem] uppercase tracking-[0.15em] text-steel-edge">
                        {pin.coords}
                      </p>
                    </HoverCardContent>
                  </HoverCard>
                </div>)}

              <div className="absolute bottom-4 left-4 z-20 flex items-center gap-2">
                <div className="h-px w-8 bg-white/60" />
                <span className="font-mono text-[0.6875rem] uppercase tracking-[0.15em] text-steel-edge">
                  5 MI
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}